import * as React from "react";
import { Pencil } from "lucide-react";
import { TaskStatusBadge } from "./TaskStatusBadge";
import { TaskTypeBadge } from "./TaskTypeBadge";
import { TaskPriorityIndicator } from "./TaskPriorityIndicator";
import { TASK_PRIORITY_LABELS, type TaskDetailDto } from "@/lib/tasks";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";

export interface TaskHeaderProps {
  task: TaskDetailDto;
}

interface HeaderField {
  label: string;
  value: React.ReactNode;
}

export function TaskHeader({ task }: TaskHeaderProps) {
  const fields: HeaderField[] = [
    {
      label: "Task",
      value: (
        <span className="inline-flex items-center gap-2">
          <span className="font-semibold">T-{task.id}</span>
          <TaskStatusBadge status={task.status} />
        </span>
      ),
    },
    { label: "Title", value: task.title },
    { label: "Type", value: <TaskTypeBadge taskType={task.taskType} /> },
    { label: "Priority", value: <span className="inline-flex items-center gap-2"><TaskPriorityIndicator priority={task.priority} /> {TASK_PRIORITY_LABELS[task.priority] ?? "Normal"}</span> },
    { label: "Assignee", value: task.assignedEmployeeName ?? "Unassigned" },
    { label: "Due Date", value: task.dueDateUtc ? formatDate(task.dueDateUtc) : "—" },
    {
      label: "Created",
      value: (
        <div className="flex flex-col">
          <span>{formatDate(task.createdOnUtc)}</span>
          {task.createdByName && <span className="text-xs text-slate-500">by {task.createdByName}</span>}
        </div>
      ),
    },
  ];

  if (task.completedOnUtc) {
    fields.push({
      label: "Completed",
      value: (
        <div className="flex flex-col">
          <span>{formatDate(task.completedOnUtc)}</span>
          {task.completedByName && <span className="text-xs text-slate-500">by {task.completedByName}</span>}
        </div>
      ),
    });
  }

  return (
    <header className="mb-4 relative bg-white rounded-lg px-6 py-4 shadow-sm border border-slate-200 group">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2"
        title="Edit Task"
      >
        <Pencil className="h-4 w-4 text-slate-500" />
      </Button>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 xl:gap-x-4">
        {fields.map((f) => (
          <div key={f.label} className="min-w-0">
            <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
              {f.label}
            </dt>
            <dd className="text-sm font-medium text-slate-900">{f.value}</dd>
          </div>
        ))}
      </dl>
    </header>
  );
}
